/**
 * Landing-page blog post preview + HTML exporter.
 *
 * Keeps the blog editor in lockstep with how posts actually render on
 * https://heistbrain.com/blog/ — same fonts (Instrument Serif + DM Sans),
 * same container (max-w 720, dark bg), same typography scale, same
 * category pill + CTA box. When the user clicks "Landing Preview" in
 * the editor they see EXACTLY how the article will look after it goes
 * live on the marketing site.
 *
 * Also exposes a static-HTML exporter that emits a full .html file
 * matching /heistbrain.com/blog/*.html so the marketing workflow is:
 * write in the app → hit Export → drop the file into the landing repo.
 */

import { escapeHtml } from "./escapeHtml.js";

// ─── Shared style constants (kept byte-for-byte with the landing CSS) ──
// If you change any of these values, update
// /heistbrain.com/blog/*.html to match so the preview stays truthful.
const LANDING_CSS = `
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box}
:root{--bg:#06050C;--bg2:#0C0A18;--accent:#8B5CF6;--accent-hi:#A78BFA;--accent-lo:#6D28D9;--text:#F1F0F5;--text2:#908CA1;--border:rgba(255,255,255,0.06);--border2:rgba(255,255,255,0.12);--fd:'Instrument Serif',Georgia,serif;--fb:'DM Sans',system-ui,sans-serif;--fm:'JetBrains Mono',monospace}
html{scroll-behavior:smooth}
body{font-family:var(--fb);background:var(--bg);color:var(--text);line-height:1.65;-webkit-font-smoothing:antialiased}
.blog-post{max-width:720px;margin:0 auto;padding:80px 20px 80px}
.blog-post-category{display:inline-block;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:16px}
.cat-strategy{background:rgba(74,222,128,0.15);color:#4ade80}
.cat-product{background:rgba(139,92,246,0.15);color:#8b5cf6}
.cat-playbook{background:rgba(251,191,36,0.15);color:#fbbf24}
.cat-story{background:rgba(59,130,246,0.15);color:#60a5fa}
.cat-tutorial{background:rgba(236,72,153,0.15);color:#ec4899}
.cat-news{background:rgba(239,68,68,0.15);color:#f87171}
.cat-guide{background:rgba(20,184,166,0.15);color:#2dd4bf}
.cat-insights{background:rgba(168,85,247,0.15);color:#a855f7}
.cat-announcement{background:rgba(249,115,22,0.15);color:#f97316}
.cat-custom{background:rgba(148,163,184,0.15);color:#94a3b8}
.blog-post h1{font-family:var(--fb);font-size:36px;font-weight:800;line-height:1.2;color:white;margin-bottom:16px;letter-spacing:-0.02em}
@media(max-width:600px){.blog-post h1{font-size:28px}}
.blog-post-meta{font-size:14px;color:rgba(255,255,255,0.4);margin-bottom:40px;padding-bottom:24px;border-bottom:1px solid rgba(255,255,255,0.08)}
.blog-post-hero{width:100%;border-radius:16px;overflow:hidden;margin-bottom:40px}
.blog-post-hero img,.blog-post-hero video{width:100%;display:block}
.blog-post-body{font-size:17px;line-height:1.8;color:rgba(255,255,255,0.75)}
.blog-post-body h2{font-family:var(--fb);font-size:24px;font-weight:700;color:white;margin:40px 0 16px;letter-spacing:-0.01em}
.blog-post-body h3{font-family:var(--fb);font-size:20px;font-weight:700;color:white;margin:32px 0 12px}
.blog-post-body p{margin-bottom:20px}
.blog-post-body strong{color:white;font-weight:600}
.blog-post-body em{color:rgba(255,255,255,0.85)}
.blog-post-body ul,.blog-post-body ol{margin:0 0 20px 24px}
.blog-post-body li{margin-bottom:8px}
.blog-post-body blockquote{border-left:3px solid var(--accent);margin:24px 0;padding:4px 20px;color:rgba(255,255,255,0.85);font-style:italic}
.blog-post-body a{color:var(--accent-hi);text-decoration:none;border-bottom:1px solid rgba(167,139,250,0.3);transition:border-color 0.2s}
.blog-post-body a:hover{border-color:var(--accent-hi)}
.blog-post-body img,.blog-post-body video{max-width:100%;border-radius:12px;margin:24px 0;display:block}
.blog-post-body figure{margin:24px 0}
.blog-post-body figcaption{font-size:13px;color:rgba(255,255,255,0.45);text-align:center;margin-top:8px;font-style:italic}
.blog-post-body pre{background:#0C0A18;border:1px solid rgba(255,255,255,0.08);border-radius:10px;padding:16px 20px;overflow-x:auto;margin:20px 0;font-family:var(--fm);font-size:14px;color:rgba(255,255,255,0.85)}
.blog-post-body code{font-family:var(--fm);font-size:0.92em;background:rgba(139,92,246,0.10);padding:2px 6px;border-radius:4px;color:#c4b5fd}
.blog-post-body hr{border:none;border-top:1px solid rgba(255,255,255,0.10);margin:40px 0}
.blog-post-cta{margin-top:48px;padding:32px;text-align:center;background:rgba(124,58,237,0.1);border:1px solid rgba(124,58,237,0.25);border-radius:16px}
.blog-post-cta h3{font-size:22px;font-weight:700;color:white;margin-bottom:12px}
.blog-post-cta p{color:rgba(255,255,255,0.6);margin-bottom:20px;font-size:15px}
.blog-back{display:inline-block;color:#a78bfa;font-size:14px;font-weight:600;text-decoration:none;margin-bottom:24px}
.blog-back:hover{color:#c4b5fd}
.btn-primary{display:inline-flex;align-items:center;gap:10px;padding:16px 32px;background:linear-gradient(135deg,var(--accent),var(--accent-lo));color:white;border:none;border-radius:14px;font-family:var(--fb);font-size:16px;font-weight:600;cursor:pointer;text-decoration:none;box-shadow:0 0 30px rgba(139,92,246,0.25);transition:all 0.3s}
.btn-primary:hover{transform:translateY(-2px);box-shadow:0 0 50px rgba(139,92,246,0.4)}
`;

const CATEGORY_OPTIONS = [
    { key: 'none', label: 'None', cls: '' },
    { key: 'product', label: 'Product Deep Dive', cls: 'cat-product' },
    { key: 'strategy', label: 'Strategy', cls: 'cat-strategy' },
    { key: 'playbook', label: 'Playbook', cls: 'cat-playbook' },
    { key: 'story', label: 'Customer Story', cls: 'cat-story' },
    { key: 'tutorial', label: 'Tutorial', cls: 'cat-tutorial' },
    { key: 'news', label: 'News', cls: 'cat-news' },
    { key: 'guide', label: 'Guide', cls: 'cat-guide' },
    { key: 'insights', label: 'Insights', cls: 'cat-insights' },
    { key: 'announcement', label: 'Announcement', cls: 'cat-announcement' },
    { key: 'custom', label: 'Custom...', cls: 'cat-custom' },
];

export function getCategoryMeta(key) {
    // For custom categories, the key IS the custom label text
    const found = CATEGORY_OPTIONS.find((c) => c.key === key);
    if (found) return found;
    // Custom value — return it as its own label
    return { key, label: key, cls: 'cat-custom' };
}

export function getCategoryOptions() {
    return CATEGORY_OPTIONS;
}

function formatLandingDate(iso) {
    if (!iso) return 'Today';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'Today';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Render a full <article class="blog-post">...</article> block using the
 * landing page structure. Caller is responsible for wrapping it in a
 * container or modal and injecting the LANDING_CSS into that scope.
 */
export function renderLandingBlogArticle(post = {}, opts = {}) {
    const {
        title = 'Untitled Blog Post',
        content_html = '',
        category = 'product',
        author = 'Heist Team',
        excerpt = '',
        featured_image_url = '',
        created_at = '',
        tags = null,
    } = post;
    // Multi-tag: render one pill per tag when a tags array is present; else
    // fall back to the single primary category.
    const tagList = Array.isArray(tags) && tags.length ? tags : [category];
    const pillsHtml = tagList
        .filter((t) => t && t !== 'none')
        .map((t) => { const m = getCategoryMeta(t); return `<span class="blog-post-category ${m.cls}">${escapeHtml(m.label)}</span>`; })
        .join(' ');
    const safeTitle = escapeHtml(title || 'Untitled');
    const safeAuthor = escapeHtml(author || 'Heist Team');
    const dateStr = formatLandingDate(created_at || new Date().toISOString());
    const hero = featured_image_url
        ? `<div class="blog-post-hero"><img src="${escapeHtml(featured_image_url)}" alt="${safeTitle}"></div>`
        : '';
    const ctaBlock = opts.includeCta === false ? '' : `
        <div class="blog-post-cta">
            <h3>Ready to give your content a memory?</h3>
            <p>Start your free trial. The Brain starts learning your brand from day one.</p>
            <a href="https://app.heistbrain.com" class="btn-primary">Try Heist Free for 7 Days</a>
        </div>
    `;

    return `
        <article class="blog-post">
            <a href="/blog" class="blog-back">← Back to Blog</a>
            ${pillsHtml ? `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px">${pillsHtml}</div>` : ''}
            <h1>${safeTitle}</h1>
            <div class="blog-post-meta">${safeAuthor} · ${escapeHtml(dateStr)}</div>
            ${hero}
            <div class="blog-post-body">
                ${content_html || '<p style="color:rgba(255,255,255,0.4)">Start writing to see your post rendered in the landing page style.</p>'}
            </div>
            ${ctaBlock}
            <div style="margin-top:40px;">
                <a href="/blog" class="blog-back">← Back to Blog</a>
            </div>
        </article>
    `;
}

/**
 * Open a full-screen modal that shows the post rendered in the
 * landing-page blog article style. The modal uses its own <iframe>
 * so the dark landing CSS doesn't leak into the app.
 */
export function openLandingPreviewModal(post, opts = {}) {
    closeLandingPreviewModal();
    const overlay = document.createElement('div');
    overlay.id = 'landing-preview-overlay';
    overlay.className = 'landing-preview-overlay';
    overlay.innerHTML = `
        <div class="landing-preview-shell" role="dialog" aria-label="Landing page preview">
            <div class="landing-preview-topbar">
                <div>
                    <div class="landing-preview-title">Landing page preview</div>
                    <div class="landing-preview-sub">Exactly as it will look on heistbrain.com/blog</div>
                </div>
                <div class="landing-preview-actions">
                    <button class="btn btn-secondary btn-sm" id="landing-preview-copy">Copy HTML</button>
                    <button class="btn btn-secondary btn-sm" id="landing-preview-download">Download .html</button>
                    <button class="btn btn-ghost btn-sm" id="landing-preview-close">Close</button>
                </div>
            </div>
            <iframe id="landing-preview-frame" title="Landing preview"></iframe>
        </div>
    `;
    document.body.appendChild(overlay);

    const frame = overlay.querySelector('#landing-preview-frame');
    const fullHtml = renderLandingBlogStandaloneHtml(post, opts);
    frame.srcdoc = fullHtml;

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeLandingPreviewModal();
    });
    overlay.querySelector('#landing-preview-close').addEventListener('click', closeLandingPreviewModal);
    overlay.querySelector('#landing-preview-copy').addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(fullHtml);
            const btn = overlay.querySelector('#landing-preview-copy');
            const prev = btn.textContent;
            btn.textContent = 'Copied ✓';
            setTimeout(() => { btn.textContent = prev; }, 1600);
        } catch (_) { /* ignore */ }
    });
    overlay.querySelector('#landing-preview-download').addEventListener('click', () => {
        const slug = (post.slug || (post.title || 'untitled').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')) + '.html';
        const blob = new Blob([fullHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = slug;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    });

    const esc = (e) => { if (e.key === 'Escape') { closeLandingPreviewModal(); document.removeEventListener('keydown', esc); } };
    document.addEventListener('keydown', esc);
}

export function closeLandingPreviewModal() {
    const existing = document.getElementById('landing-preview-overlay');
    if (existing) existing.remove();
}

/**
 * Build a full standalone HTML document matching the landing page
 * blog post template, ready to drop into /heistbrain.com/blog/.
 */
export function renderLandingBlogStandaloneHtml(post = {}, opts = {}) {
    const title = post.title || 'Untitled Blog Post';
    const description = post.excerpt || post.meta_description || 'A post from the Heist blog.';
    const ogImage = post.featured_image_url || 'https://heistbrain.com/og-image.png';
    const slug = post.slug || (title || 'untitled').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const article = renderLandingBlogArticle(post, opts);
    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escapeHtml(title)} — Heist Brain Blog</title>
<meta name="description" content="${escapeHtml(description)}">
<meta property="og:type" content="article">
<meta property="og:title" content="${escapeHtml(title)}">
<meta property="og:description" content="${escapeHtml(description)}">
<meta property="og:url" content="https://heistbrain.com/blog/${escapeHtml(slug)}">
<meta property="og:site_name" content="Heist">
<meta property="og:image" content="${escapeHtml(ogImage)}">
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:title" content="${escapeHtml(title)}">
<meta name="twitter:description" content="${escapeHtml(description)}">
<link rel="canonical" href="https://heistbrain.com/blog/${escapeHtml(slug)}">
<link rel="icon" href="/favicon.svg" type="image/svg+xml">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:ital,opsz,wght@0,9..40,300..800;1,9..40,300..800&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>${LANDING_CSS}</style>
</head>
<body>
${article}
</body>
</html>`;
}

export { LANDING_CSS };
